/* Explicit-source display currency for Shopify presentment prices. No cart or market mutations. */
(function (global) {
  'use strict';

  function precision(currency) {
    return currency === 'BTC' ? 8 : new Intl.NumberFormat('en', {
      style: 'currency', currency: currency
    }).resolvedOptions().maximumFractionDigits;
  }

  function minorAmount(value) {
    var amount = Number(value);
    if (value == null || value === '' || !Number.isFinite(amount) || amount < 0) {
      throw new Error('The native price amount is invalid.');
    }
    return amount;
  }

  function displayedMinorUnits(formatted) {
    var numeric = String(formatted).replace(/,/g, '').trim();
    if (!/^\d+(?:\.\d+)?$/.test(numeric)) throw new Error('Currency formatting is unavailable.');
    return Number((minorAmount(numeric) * 100).toFixed(8));
  }

  function nativeContext(base, status) {
    var digits = precision(base);
    return {
      base: base, currency: base, api: null, status: status,
      resolved: status === 'base', key: status + ':' + base, precision: digits,
      format: function (minor) { return (minorAmount(minor) / 100).toFixed(digits); },
      convert: function (minor) { return minor == null ? minor : minorAmount(minor); }
    };
  }

  function currencyContext(baseCurrency, host) {
    host = host || global;
    var base = /^[A-Z]{3}$/.test(baseCurrency || '') ? baseCurrency : 'GBP';
    var app = host.DoublyGlobalCurrency;
    var expected = !!host.DoublyGlobal || !!(host.document && typeof host.document.querySelector === 'function' &&
      host.document.querySelector('script[src*="doubly.js"]'));
    if (!app) return nativeContext(base, expected ? 'pending' : 'base');
    if (!app.currentCurrency) return nativeContext(base, 'pending');
    if (!/^[A-Z]{3}$/.test(app.currentCurrency) || typeof app.convert !== 'function' || typeof app.formatMoney !== 'function') {
      return nativeContext(base, 'unavailable');
    }
    var target = app.currentCurrency;
    var context = { base: base, currency: target, api: app, status: 'ready', resolved: true };
    context.format = function (minor) {
      var amount = minorAmount(minor);
      // Shopify's native market price is authoritative. Do not apply foreign-currency .99 rounding to it.
      if (target === base) return (amount / 100).toFixed(precision(base));
      var converted = minorAmount(app.convert(amount, base, target));
      var formatted = app.formatMoney(converted, '{{amount}}', false, false);
      displayedMinorUnits(formatted);
      return String(formatted);
    };
    context.convert = function (minor) { return minor == null ? minor : displayedMinorUnits(context.format(minor)); };
    try {
      var probe = context.format(12345);
      context.precision = (probe.split('.')[1] || '').length;
      var rate = target === base ? 10000 : Number(app.convert(10000, base, target));
      if (!Number.isFinite(rate) || rate <= 0) throw new Error('Currency conversion is unavailable.');
      context.key = base + ':' + target + ':app:' + rate + ':' + probe;
    } catch (_) { return nativeContext(base, 'unavailable'); }
    return context;
  }

  function nodesWithin(root, selector) {
    var nodes = root && typeof root.querySelectorAll === 'function' ? Array.from(root.querySelectorAll(selector)) : [];
    if (root && typeof root.matches === 'function' && root.matches(selector)) nodes.unshift(root);
    return nodes;
  }

  function formatManagedPrices(root, context, options) {
    options = options || {};
    var host = options.host || global;
    var source = options.sourceCurrency || context && context.base || 'GBP';
    var locale = options.locale || host.document && host.document.documentElement && host.document.documentElement.lang || 'en-GB';
    context = context || currencyContext(source, host);

    // Older cached records can supply the exact current amount, never parse a rendered money string.
    if (options.currentAmount != null) {
      var current = nodesWithin(root, '.price__current')[0];
      if (!current) current = nodesWithin(root, 'span.money').find(function (node) {
        return !node.classList.contains('unit-price__unit') && !node.classList.contains('unit-price__price');
      });
      if (current && !current.hasAttribute('data-tlt-money-amount')) {
        current.setAttribute('data-tlt-money-amount', String(minorAmount(options.currentAmount)));
        current.setAttribute('data-tlt-money-currency', source);
        current.innerHTML = new Intl.NumberFormat(locale, { style: 'currency', currency: source }).format(Number(options.currentAmount) / 100);
      }
    }

    var nodes = nodesWithin(root, '[data-tlt-money-amount][data-tlt-money-currency]');
    var unknown = nodesWithin(root, '.price__was, .unit-price__price, span.money').filter(function (node) {
      return !node.classList.contains('unit-price__unit') && String(node.textContent || '').trim() &&
        !node.hasAttribute('data-tlt-money-amount');
    });
    // A cached sale/unit amount without metadata cannot safely be converted. Keep that whole price native.
    var incomplete = unknown.length > 0;
    var staged = nodes.map(function (node) {
      var nativeAmount = minorAmount(node.getAttribute('data-tlt-money-amount'));
      var nativeCurrency = node.getAttribute('data-tlt-money-currency');
      if (!/^[A-Z]{3}$/.test(nativeCurrency || '')) throw new Error('The native price currency is invalid.');
      var nativeKey = nativeCurrency + ':' + nativeAmount;
      if (!node.tltNativeMoney || node.tltNativeMoney.key !== nativeKey) {
        // Variant changes replace both metadata and native markup in the same synchronous update.
        node.tltNativeMoney = { key: nativeKey, html: node.innerHTML };
      }
      var local = nativeCurrency === context.base ? context : currencyContext(nativeCurrency, host);
      var useNative = incomplete || !context.api || !context.resolved || !local.resolved || local.currency === nativeCurrency;
      var displayCurrency = useNative ? nativeCurrency : local.currency;
      var text = useNative ? null : new Intl.NumberFormat(locale, {
        style: 'currency', currency: displayCurrency,
        minimumFractionDigits: local.precision, maximumFractionDigits: local.precision
      }).format(local.convert(nativeAmount) / 100);
      var nativeHtml = node.tltNativeMoney.html;
      var selectedCurrency = host.DoublyGlobalCurrency && host.DoublyGlobalCurrency.currentCurrency;
      if (useNative && selectedCurrency && selectedCurrency !== nativeCurrency && !nativeHtml.includes(nativeCurrency)) {
        nativeHtml += ' ' + nativeCurrency;
      }
      return { node: node, html: useNative ? nativeHtml : null, text: text, currency: displayCurrency };
    });
    // Validate the full price before changing any of its amounts; there is never a half-converted sale price.
    staged.forEach(function (entry) {
      entry.node.classList.remove('money');
      entry.node.removeAttribute('doubly-currency');
      if (entry.html != null) {
        if (entry.node.innerHTML !== entry.html) entry.node.innerHTML = entry.html;
      } else if (entry.node.textContent !== entry.text) entry.node.textContent = entry.text;
      entry.node.setAttribute('data-tlt-money-display-currency', entry.currency);
    });
    nodesWithin(root, 'span.money').forEach(function (node) { node.classList.remove('money'); });
    if (incomplete) unknown.forEach(function (node) {
      if (!node.tltUnknownNativeHtml) node.tltUnknownNativeHtml = node.innerHTML;
      var html = node.tltUnknownNativeHtml;
      if (!html.includes(source)) html += ' ' + source;
      if (node.innerHTML !== html) node.innerHTML = html;
    });
    return { formatted: staged.length, incomplete: incomplete, unavailable: !context.resolved || incomplete };
  }

  var api = { currencyContext: currencyContext, formatManagedPrices: formatManagedPrices };
  global.TLTCurrency = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
